import { env } from "@/core/config/env";
import { createUuid } from "@/core/utils/uuid";
import type {
  DirectoryMembershipPage,
  DirectoryMembershipQuery,
  DirectoryTeam,
  TenantPermissionModule,
  TenantRole,
  UpsertTenantRoleRequest,
} from "@/core/types/directory";
import { demoDirectory } from "@/mock/mockAdapter";
import { httpClient } from "./httpClient";

const buildQuery = (query: DirectoryMembershipQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      params.set(key, String(value));
  });
  const result = params.toString();
  return result ? `?${result}` : "";
};

const demoPermissionModules: TenantPermissionModule[] = [
  {
    code: "CORE",
    title: "دسترسی‌های پایه",
    description: "برای ورود و مشاهده اطلاعات پایه سازمان ضروری است.",
    featureCode: null,
    isCore: true,
    permissions: [
      {
        code: "identity.me.read",
        title: "مشاهده پروفایل خود",
        description: "کاربر بتواند پروفایل ورود خود را مشاهده کند.",
      },
      {
        code: "tenancy.read",
        title: "مشاهده اطلاعات سازمان",
        description: "اطلاعات عمومی سازمان در اختیار کاربر باشد.",
      },
    ],
  },
  {
    code: "LEADS",
    title: "سرنخ‌ها",
    description: "ثبت و پیگیری سرنخ‌های فروش.",
    featureCode: "LEADS",
    isCore: false,
    permissions: [
      {
        code: "leads.read",
        title: "مشاهده سرنخ‌ها",
        description: "مشاهده فهرست سرنخ‌ها.",
      },
      {
        code: "leads.create",
        title: "ثبت سرنخ",
        description: "ایجاد سرنخ جدید.",
      },
      {
        code: "leads.update",
        title: "ویرایش سرنخ",
        description: "ویرایش سرنخ‌های مجاز.",
      },
    ],
  },
];

let demoRoles: TenantRole[] = [
  {
    id: "sales-agent",
    code: "sales-agent",
    name: "کارشناس فروش",
    isSystemRole: true,
    isActive: true,
    memberCount: 0,
    permissions: [
      { code: "identity.me.read", scope: "tenant" },
      { code: "tenancy.read", scope: "tenant" },
      { code: "leads.read", scope: "own" },
      { code: "leads.create", scope: "own" },
      { code: "leads.update", scope: "own" },
    ],
  },
];

export const directoryApi = {
  async memberships(
    query: DirectoryMembershipQuery = {},
  ): Promise<DirectoryMembershipPage> {
    if (env.isDemo) {
      const search = query.search?.trim().toLowerCase();
      const items = demoDirectory
        .filter(
          (item) => !search || item.displayName.toLowerCase().includes(search),
        )
        .map((item) => ({
          membershipId: item.membershipId,
          userId: item.membershipId,
          username: item.displayName.toLowerCase().replace(/\s+/g, "."),
          displayName: item.displayName,
          jobTitle: null,
          status: "Active",
          email: null,
          mobile: null,
          branchId: null,
          teamId: item.teamId,
          teamName: item.teamName,
          teams: [{ id: item.teamId, name: item.teamName }],
          roleCode: "sales-agent",
          roleName: "کارشناس فروش",
          isSystemRole: true,
        }));
      return {
        page: 1,
        pageSize: items.length || 25,
        totalCount: items.length,
        items,
      };
    }

    return (
      await httpClient.get<DirectoryMembershipPage>(
        `/api/v1/directory/memberships${buildQuery(query)}`,
      )
    ).data;
  },

  async teams(search?: string): Promise<DirectoryTeam[]> {
    if (env.isDemo) {
      const unique = new Map<string, DirectoryTeam>();
      demoDirectory.forEach((item) =>
        unique.set(item.teamId, {
          id: item.teamId,
          name: item.teamName,
          branchId: null,
          isActive: true,
        }),
      );
      return [...unique.values()];
    }

    const query = search?.trim()
      ? `?search=${encodeURIComponent(search.trim())}&activeOnly=true`
      : "?activeOnly=true";
    return (
      await httpClient.get<DirectoryTeam[]>(`/api/v1/directory/teams${query}`)
    ).data;
  },
  async createTeam(name: string): Promise<DirectoryTeam> {
    if (env.isDemo)
      return { id: createUuid(), name, branchId: null, isActive: true };
    return (
      await httpClient.post<DirectoryTeam>("/api/v1/teams", {
        name,
        branchId: null,
        isActive: true,
      })
    ).data;
  },
  async updateTeam(
    id: string,
    name: string,
    isActive = true,
  ): Promise<DirectoryTeam> {
    if (env.isDemo) return { id, name, branchId: null, isActive };
    return (
      await httpClient.request<DirectoryTeam>(`/api/v1/teams/${id}`, {
        method: "PUT",
        body: { name, branchId: null, isActive },
      })
    ).data;
  },
  async deleteTeam(id: string): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(`/api/v1/teams/${id}`, { method: "DELETE" });
  },
  async assignMembershipTeam(
    membershipId: string,
    teamId: string | null,
  ): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(
      `/api/v1/teams/memberships/${membershipId}`,
      { method: "PUT", body: { teamId } },
    );
  },
  async roles(): Promise<TenantRole[]> {
    if (env.isDemo) return demoRoles;
    return (await httpClient.get<TenantRole[]>("/api/v1/tenant-roles")).data;
  },
  async permissionModules(): Promise<TenantPermissionModule[]> {
    if (env.isDemo) return demoPermissionModules;
    return (
      await httpClient.get<TenantPermissionModule[]>(
        "/api/v1/tenant-roles/permission-modules",
      )
    ).data;
  },
  async createRole(request: UpsertTenantRoleRequest): Promise<TenantRole> {
    if (env.isDemo) {
      const role: TenantRole = {
        id: createUuid(),
        code: `custom-${Date.now()}`,
        name: request.name,
        isSystemRole: false,
        isActive: request.isActive,
        memberCount: 0,
        permissions: request.permissions,
      };
      demoRoles = [...demoRoles, role];
      return role;
    }
    return (await httpClient.post<TenantRole>("/api/v1/tenant-roles", request))
      .data;
  },
  async updateRole(
    id: string,
    request: UpsertTenantRoleRequest,
  ): Promise<TenantRole> {
    if (env.isDemo) {
      const current = demoRoles.find((item) => item.id === id);
      if (!current) throw new Error("نقش موردنظر پیدا نشد.");
      const updated = {
        ...current,
        name: request.name,
        isActive: request.isActive,
        permissions: request.permissions,
      };
      demoRoles = demoRoles.map((item) => (item.id === id ? updated : item));
      return updated;
    }
    return (
      await httpClient.request<TenantRole>(`/api/v1/tenant-roles/${id}`, {
        method: "PUT",
        body: request,
      })
    ).data;
  },
  async deleteRole(id: string): Promise<void> {
    if (env.isDemo) {
      demoRoles = demoRoles.filter((item) => item.id !== id);
      return;
    }
    await httpClient.request<void>(`/api/v1/tenant-roles/${id}`, {
      method: "DELETE",
    });
  },
};
